import { useState, useEffect, useCallback } from "react";
import {
  Building2, Plus, RefreshCw, Users, CreditCard, Check, X,
  AlertTriangle, Play, Pause, ExternalLink, Copy, Mail,
  Shield, Zap, BookOpen, TrendingUp, ChevronDown, ChevronUp,
  Settings, Clock, CheckCircle, XCircle, DollarSign, Key, UserX, UserCheck,
  Trash2
} from "lucide-react";
import getApiUrl from '../../utils/getApiUrl';

const API = `${getApiUrl()}/api`;

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};

// Helper pour eviter "body stream already read"
const safeJson = async (response) => {
  try {
    return await response.clone().json();
  } catch {
    try {
      const text = await response.text();
      return JSON.parse(text);
    } catch {
      return null;
    }
  }
};

// Couleurs par statut billing
const STATUS_COLORS = {
  active: { bg: "bg-emerald-500/20", text: "text-emerald-400", border: "border-emerald-500/30" },
  pending_setup: { bg: "bg-amber-500/20", text: "text-amber-400", border: "border-amber-500/30" },
  past_due: { bg: "bg-red-500/20", text: "text-red-400", border: "border-red-500/30" },
  suspended: { bg: "bg-red-500/20", text: "text-red-400", border: "border-red-500/30" },
  canceled: { bg: "bg-slate-500/20", text: "text-slate-400", border: "border-slate-500/30" },
};

const STATUS_LABELS = {
  active: "Actif",
  pending_setup: "En attente setup",
  past_due: "Paiement en retard",
  suspended: "Suspendu",
  canceled: "Annule",
};

const MODULES = {
  commercial: { name: "Commercial", icon: TrendingUp, color: "blue" },
  compta: { name: "Compta", icon: DollarSign, color: "emerald" },
  marketing: { name: "Marketing", icon: Zap, color: "purple" },
  formation: { name: "Formation", icon: BookOpen, color: "amber" },
};

export default function TenantsManager() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOnboardForm, setShowOnboardForm] = useState(false);
  const [onboarding, setOnboarding] = useState(false);
  const [onboardResult, setOnboardResult] = useState(null);
  const [expandedTenant, setExpandedTenant] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  
  // Gestion des utilisateurs
  const [tenantUsers, setTenantUsers] = useState({});
  const [showUserForm, setShowUserForm] = useState(null); // tenant_id or null
  const [userFormLoading, setUserFormLoading] = useState(false);
  const [userForm, setUserForm] = useState({
    email: "",
    password: "",
    name: "",
    role: "user",
  });
  const [deleteConfirm, setDeleteConfirm] = useState(null); // tenant_id en attente de confirmation
  
  // Formulaire onboarding
  const [form, setForm] = useState({
    company_name: "",
    contact_email: "",
    include_audit: false,
  });

  const loadTenants = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      const r = await authFetch(`${API}/billing/admin/overview`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (r.ok) {
        const data = await safeJson(r);
        setTenants(data?.tenants || []);
      } else {
        const err = await safeJson(r) || {};
        console.error("loadTenants error:", r.status, err);
      }
    } catch (e) {
      console.error("Erreur chargement tenants:", e);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  // Creer un nouveau tenant
  const handleOnboard = async () => {
    if (!form.company_name.trim() || !form.contact_email.trim()) return;
    setOnboarding(true);
    setOnboardResult(null);
    try {
      const token = localStorage.getItem("auth_token");
      const r = await authFetch(`${API}/billing/admin/onboard`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(form),
      });
      if (r.ok) {
        const data = await safeJson(r);
        setOnboardResult(data);
        loadTenants();
      } else {
        const err = await safeJson(r);
        setOnboardResult({ error: err?.detail || "Erreur lors de la creation" });
      }
    } catch (e) {
      setOnboardResult({ error: e.message });
    }
    setOnboarding(false);
  };

  // Activer manuellement un tenant
  const handleForceActivate = async (tenantId) => {
    if (!window.confirm("Activer ce tenant manuellement (bypass paiement) ?")) return;
    setActionLoading(tenantId);
    try {
      const token = localStorage.getItem("auth_token");
      await authFetch(`${API}/billing/admin/force-activate/${tenantId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ include_audit: false }),
      });
      loadTenants();
    } catch (e) {
      console.error(e);
    }
    setActionLoading(null);
  };

  // Suspendre un tenant
  const handleSuspend = async (tenantId) => {
    const reason = window.prompt("Raison de la suspension (optionnel):");
    if (reason === null) return;
    setActionLoading(tenantId);
    try {
      const token = localStorage.getItem("auth_token");
      await authFetch(`${API}/billing/admin/suspend`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ tenant_id: tenantId, reason }),
      });
      loadTenants();
    } catch (e) {
      console.error(e);
    }
    setActionLoading(null);
  };

  // Reactiver un tenant
  const handleReactivate = async (tenantId) => {
    if (!window.confirm("Reactiver ce tenant ?")) return;
    setActionLoading(tenantId);
    try {
      const token = localStorage.getItem("auth_token");
      await authFetch(`${API}/billing/admin/reactivate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ tenant_id: tenantId }),
      });
      loadTenants();
    } catch (e) {
      console.error(e);
    }
    setActionLoading(null);
  };

  // Supprimer un tenant (avec double confirmation)
  const handleDeleteTenant = async (tenantId) => {
    if (tenantId === "tenant_default") {
      alert("Impossible de supprimer le tenant principal");
      return;
    }
    setActionLoading(`delete-${tenantId}`);
    try {
      const token = localStorage.getItem("auth_token");
      const r = await authFetch(`${API}/admin/tenants/${tenantId}`, {
        method: "DELETE",
        credentials: "include",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      const data = await safeJson(r);
      if (!r.ok) {
        alert(`Erreur suppression: ${data.detail || JSON.stringify(data)}`);
      } else {
        loadTenants();
      }
    } catch (e) {
      alert(`Erreur: ${e.message}`);
    } finally {
      setActionLoading(null);
      setDeleteConfirm(null);
    }
  };

  // Toggle un module
  const handleToggleModule = async (tenantId, moduleId, currentlyEnabled) => {
    setActionLoading(`${tenantId}-${moduleId}`);
    try {
      const token = localStorage.getItem("auth_token");
      const endpoint = currentlyEnabled ? "disable" : "enable";
      const r = await authFetch(`${API}/billing/admin/module/${endpoint}`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ tenant_id: tenantId, module_id: moduleId }),
      });
      const data = await safeJson(r);
      if (!r.ok) {
        alert(`Erreur module ${moduleId}: ${data.detail || data.error || JSON.stringify(data)}`);
      } else {
        loadTenants();
      }
    } catch (e) {
      alert(`Erreur reseau: ${e.message}`);
      console.error(e);
    }
    setActionLoading(null);
  };

  // Charger les utilisateurs d'un tenant
  const loadTenantUsers = async (tenantId) => {
    try {
      const token = localStorage.getItem("auth_token");
      const r = await authFetch(`${API}/admin/tenants/${tenantId}/members`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        const data = await safeJson(r);
        setTenantUsers((prev) => ({ ...prev, [tenantId]: data.members || [] }));
      }
    } catch (e) {
      console.error("Erreur chargement users:", e);
    }
  };

  // Creer un utilisateur pour un tenant
  const handleCreateUser = async (tenantId) => {
    if (!userForm.email.trim() || !userForm.password.trim() || !userForm.name.trim()) {
      alert("Tous les champs sont requis");
      return;
    }
    setUserFormLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      const r = await authFetch(`${API}/admin/tenants/${tenantId}/users`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(userForm),
      });
      if (r.ok) {
        const data = await safeJson(r);
        alert(`Utilisateur cree: ${data.user.email}`);
        setShowUserForm(null);
        setUserForm({ email: "", password: "", name: "", role: "user" });
        loadTenantUsers(tenantId);
      } else {
        const err = await safeJson(r);
        alert("Erreur: " + (err.detail || "Erreur inconnue"));
      }
    } catch (e) {
      alert("Erreur: " + e.message);
    }
    setUserFormLoading(false);
  };

  // Reinitialiser le mot de passe d'un utilisateur
  const handleResetPassword = async (tenantId, userId, email) => {
    const newPassword = window.prompt(`Nouveau mot de passe pour ${email}:`);
    if (!newPassword || newPassword.trim().length < 4) {
      if (newPassword !== null) alert("Le mot de passe doit faire au moins 4 caracteres");
      return;
    }
    setActionLoading(`reset-${userId}`);
    try {
      const token = localStorage.getItem("auth_token");
      const r = await authFetch(`${API}/admin/tenants/${tenantId}/users/${userId}/reset-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ new_password: newPassword.trim() }),
      });
      if (r.ok) {
        alert(`Mot de passe reinitialise pour ${email}\nNouveau: ${newPassword.trim()}`);
      } else {
        const err = await safeJson(r);
        alert("Erreur: " + (err.detail || "Erreur inconnue"));
      }
    } catch (e) {
      alert("Erreur: " + e.message);
    }
    setActionLoading(null);
  };

  // Activer/Desactiver un utilisateur
  const handleToggleUserStatus = async (tenantId, userId, currentStatus, email) => {
    const newStatus = !currentStatus;
    const action = newStatus ? "activer" : "desactiver";
    if (!window.confirm(`Voulez-vous ${action} l'utilisateur ${email} ?`)) return;
    
    setActionLoading(`status-${userId}`);
    try {
      const token = localStorage.getItem("auth_token");
      const r = await authFetch(`${API}/admin/tenants/${tenantId}/users/${userId}/toggle-status`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ is_active: newStatus }),
      });
      if (r.ok) {
        loadTenantUsers(tenantId);
      } else {
        const err = await safeJson(r);
        alert("Erreur: " + (err.detail || "Erreur inconnue"));
      }
    } catch (e) {
      alert("Erreur: " + e.message);
    }
    setActionLoading(null);
  };

  // Copier dans le presse-papier
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert("Copie dans le presse-papier !");
  };

  // Stats globales
  const stats = {
    total: tenants.length,
    active: tenants.filter((t) => t.billing_status === "active").length,
    pending: tenants.filter((t) => t.billing_status === "pending_setup").length,
    problem: tenants.filter((t) => ["past_due", "suspended"].includes(t.billing_status)).length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Building2 className="w-6 h-6 text-blue-400" />
            Gestion des Tenants (Clients SaaS)
          </h2>
          <p className="text-sm text-slate-400 mt-1">
            Onboarding, facturation et gestion des abonnements
          </p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="new-tenant-btn"
            onClick={() => {
              setShowOnboardForm(true);
              setOnboardResult(null);
              setForm({ company_name: "", contact_email: "", include_audit: false });
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm transition-colors"
          >
            <Plus className="w-4 h-4" />
            Nouveau Tenant
          </button>
          <button
            onClick={loadTenants}
            className="p-2 bg-slate-800 hover:bg-slate-700 rounded-lg"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
          <div className="flex items-center gap-3 mb-2">
            <Building2 className="w-5 h-5 text-slate-400" />
            <span className="text-sm text-slate-400">Total Tenants</span>
          </div>
          <div className="text-2xl font-bold text-white">{stats.total}</div>
        </div>
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
          <div className="flex items-center gap-3 mb-2">
            <CheckCircle className="w-5 h-5 text-emerald-400" />
            <span className="text-sm text-emerald-400">Actifs</span>
          </div>
          <div className="text-2xl font-bold text-emerald-400">{stats.active}</div>
        </div>
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
          <div className="flex items-center gap-3 mb-2">
            <Clock className="w-5 h-5 text-amber-400" />
            <span className="text-sm text-amber-400">En attente</span>
          </div>
          <div className="text-2xl font-bold text-amber-400">{stats.pending}</div>
        </div>
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
          <div className="flex items-center gap-3 mb-2">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <span className="text-sm text-red-400">Problemes</span>
          </div>
          <div className="text-2xl font-bold text-red-400">{stats.problem}</div>
        </div>
      </div>

      {/* Formulaire Onboarding */}
      {showOnboardForm && (
        <div className="bg-gradient-to-br from-blue-900/30 to-cyan-900/30 border border-blue-500/30 rounded-xl p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-semibold text-white flex items-center gap-2">
              <Plus className="w-5 h-5 text-blue-400" />
              Creer un nouveau Tenant
            </h3>
            <button
              onClick={() => setShowOnboardForm(false)}
              className="p-1 hover:bg-slate-700 rounded"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>

          {!onboardResult ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">
                    Nom de l'entreprise *
                  </label>
                  <input
                    data-testid="tenant-company-name"
                    type="text"
                    value={form.company_name}
                    onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                    placeholder="Ex: ACME Corporation"
                    className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">
                    Email du contact *
                  </label>
                  <input
                    data-testid="tenant-contact-email"
                    type="email"
                    value={form.contact_email}
                    onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                    placeholder="contact@acme.fr"
                    className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white"
                  />
                </div>
              </div>

              <label className="flex items-center gap-3 p-4 bg-slate-800/50 rounded-lg cursor-pointer hover:bg-slate-800 transition-colors">
                <input
                  type="checkbox"
                  checked={form.include_audit}
                  onChange={(e) => setForm({ ...form, include_audit: e.target.checked })}
                  className="w-5 h-5 rounded border-slate-600"
                />
                <div>
                  <span className="text-white font-medium">Inclure Audit & Consulting</span>
                  <p className="text-xs text-slate-400">+2 000 EUR en plus des frais de setup (990 EUR)</p>
                </div>
              </label>

              <div className="bg-slate-800/50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-slate-300 mb-2">Resume de la commande</h4>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Frais de setup</span>
                  <span className="text-white">990 EUR</span>
                </div>
                {form.include_audit && (
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-slate-400">Audit & Consulting</span>
                    <span className="text-white">2 000 EUR</span>
                  </div>
                )}
                <div className="border-t border-slate-700 mt-2 pt-2 flex justify-between font-bold">
                  <span className="text-white">Total</span>
                  <span className="text-blue-400">
                    {form.include_audit ? "2 990" : "990"} EUR
                  </span>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowOnboardForm(false)}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm"
                >
                  Annuler
                </button>
                <button
                  data-testid="create-tenant-btn"
                  onClick={handleOnboard}
                  disabled={onboarding || !form.company_name.trim() || !form.contact_email.trim()}
                  className="flex items-center gap-2 px-6 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white rounded-lg text-sm transition-colors"
                >
                  {onboarding ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <CreditCard className="w-4 h-4" />
                  )}
                  Creer & Generer lien Stripe
                </button>
              </div>
            </div>
          ) : onboardResult.error ? (
            <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-4">
              <div className="flex items-center gap-2 text-red-400 mb-2">
                <XCircle className="w-5 h-5" />
                <span className="font-medium">Erreur</span>
              </div>
              <p className="text-sm text-red-300">{onboardResult.error}</p>
              <button
                onClick={() => setOnboardResult(null)}
                className="mt-3 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm"
              >
                Reessayer
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-lg p-4">
                <div className="flex items-center gap-2 text-emerald-400 mb-2">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">Tenant cree avec succes !</span>
                </div>
                <p className="text-sm text-slate-300">
                  <strong>{onboardResult.company_name}</strong> a ete cree.
                </p>
              </div>

              <div className="bg-slate-800/50 rounded-lg p-4 space-y-3">
                <div>
                  <span className="text-xs text-slate-400">Tenant ID</span>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="text-sm text-blue-400 bg-slate-900 px-2 py-1 rounded">
                      {onboardResult.tenant_id}
                    </code>
                    <button
                      onClick={() => copyToClipboard(onboardResult.tenant_id)}
                      className="p-1 hover:bg-slate-700 rounded"
                    >
                      <Copy className="w-4 h-4 text-slate-400" />
                    </button>
                  </div>
                </div>

                {onboardResult.checkout_url && (
                  <div>
                    <span className="text-xs text-slate-400">Lien de paiement Stripe</span>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="text"
                        value={onboardResult.checkout_url}
                        readOnly
                        className="flex-1 text-sm bg-slate-900 border border-slate-700 rounded px-3 py-2 text-slate-300"
                      />
                      <button
                        onClick={() => copyToClipboard(onboardResult.checkout_url)}
                        className="p-2 bg-slate-700 hover:bg-slate-600 rounded"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <a
                        href={onboardResult.checkout_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 bg-blue-600 hover:bg-blue-500 rounded"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                    <p className="text-xs text-amber-400 mt-2">
                      Envoyez ce lien au client pour qu'il procede au paiement.
                    </p>
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowOnboardForm(false);
                    setOnboardResult(null);
                  }}
                  className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm"
                >
                  Fermer
                </button>
                <button
                  data-testid="force-activate-new-tenant"
                  onClick={() => handleForceActivate(onboardResult.tenant_id)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-sm"
                >
                  <Play className="w-4 h-4" />
                  Activer manuellement (test)
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Liste des Tenants */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-slate-500" />
        </div>
      ) : tenants.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <Building2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>Aucun tenant configure</p>
          <p className="text-xs mt-1">Cliquez sur "Nouveau Tenant" pour commencer</p>
        </div>
      ) : (
        <div className="space-y-3">
          {tenants.map((tenant) => {
            const status = STATUS_COLORS[tenant.billing_status] || STATUS_COLORS.pending_setup;
            const isExpanded = expandedTenant === tenant.tenant_id;

            return (
              <div
                key={tenant.tenant_id}
                data-testid={`tenant-card-${tenant.tenant_id}`}
                className={`bg-slate-800/50 border rounded-xl overflow-hidden transition-all ${status.border}`}
              >
                {/* Header */}
                <div
                  className="p-4 cursor-pointer hover:bg-slate-800/70 transition-colors"
                  onClick={() => setExpandedTenant(isExpanded ? null : tenant.tenant_id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${status.bg}`}>
                        <Building2 className={`w-5 h-5 ${status.text}`} />
                      </div>
                      <div>
                        <h3 className="font-bold text-white">{tenant.name || tenant.tenant_id}</h3>
                        <div className="flex items-center gap-3 mt-1 text-xs">
                          <span className={`px-2 py-0.5 rounded ${status.bg} ${status.text}`}>
                            {STATUS_LABELS[tenant.billing_status] || tenant.billing_status}
                          </span>
                          {tenant.setup_paid && (
                            <span className="text-emerald-400 flex items-center gap-1">
                              <Check className="w-3 h-3" /> Setup paye
                            </span>
                          )}
                          <span className="text-slate-500">
                            {tenant.current_seats || 0} siege(s)
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      {/* Modules actifs */}
                      <div className="flex gap-1">
                        {Object.entries(tenant.modules_enabled || {}).map(([mod, enabled]) => {
                          if (!MODULES[mod]) return null;
                          const ModIcon = MODULES[mod].icon;
                          return (
                            <div
                              key={mod}
                              className={`w-7 h-7 rounded flex items-center justify-center ${
                                enabled
                                  ? `bg-${MODULES[mod].color}-500/20 text-${MODULES[mod].color}-400`
                                  : "bg-slate-700/50 text-slate-600"
                              }`}
                              title={`${MODULES[mod].name}: ${enabled ? "Actif" : "Inactif"}`}
                            >
                              <ModIcon className="w-3.5 h-3.5" />
                            </div>
                          );
                        })}
                      </div>

                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Panel etendu */}
                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-slate-700/50 bg-slate-900/30">
                    <div className="grid grid-cols-2 gap-6 pt-4">
                      {/* Infos */}
                      <div className="space-y-3">
                        <h4 className="text-sm font-medium text-slate-400 flex items-center gap-2">
                          <Shield className="w-4 h-4" />
                          Informations
                        </h4>
                        <div className="bg-slate-800/50 rounded-lg p-3 space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-slate-500">Tenant ID</span>
                            <code className="text-blue-400">{tenant.tenant_id}</code>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">Stripe Customer</span>
                            <code className="text-slate-300">
                              {tenant.stripe_customer_id || "Non configure"}
                            </code>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">Tier actuel</span>
                            <span className="text-white">{tenant.current_tier || "-"}</span>
                          </div>
                        </div>
                      </div>

                      {/* Modules */}
                      <div className="space-y-3">
                        <h4 className="text-sm font-medium text-slate-400 flex items-center gap-2">
                          <Settings className="w-4 h-4" />
                          Modules
                        </h4>
                        <div className="grid grid-cols-2 gap-2">
                          {Object.entries(MODULES).map(([modId, mod]) => {
                            const enabled = tenant.modules_enabled?.[modId];
                            const isCommercial = modId === "commercial";
                            const isLoading = actionLoading === `${tenant.tenant_id}-${modId}`;

                            return (
                              <button
                                key={modId}
                                data-testid={`toggle-module-${tenant.tenant_id}-${modId}`}
                                onClick={() =>
                                  !isCommercial && handleToggleModule(tenant.tenant_id, modId, enabled)
                                }
                                disabled={isCommercial || isLoading}
                                className={`flex items-center gap-2 p-2 rounded-lg text-sm transition-all ${
                                  enabled
                                    ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
                                    : "bg-slate-800 text-slate-400 border border-slate-700 hover:border-slate-600"
                                } ${isCommercial ? "cursor-not-allowed opacity-70" : ""}`}
                              >
                                {isLoading ? (
                                  <RefreshCw className="w-4 h-4 animate-spin" />
                                ) : (
                                  <mod.icon className="w-4 h-4" />
                                )}
                                {mod.name}
                                {isCommercial && (
                                  <span className="text-xs text-slate-500">(inclus)</span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Section Utilisateurs */}
                    <div className="mt-4 pt-4 border-t border-slate-700/50">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-slate-400 flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          Utilisateurs du tenant
                        </h4>
                        <div className="flex gap-2">
                          <button
                            onClick={() => loadTenantUsers(tenant.tenant_id)}
                            className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded text-xs flex items-center gap-1"
                          >
                            <RefreshCw className="w-3 h-3" />
                            Charger
                          </button>
                          <button
                            data-testid={`add-user-${tenant.tenant_id}`}
                            onClick={() => {
                              setShowUserForm(tenant.tenant_id);
                              setUserForm({ email: "", password: "", name: "", role: "user" });
                            }}
                            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 rounded text-xs flex items-center gap-1"
                          >
                            <Plus className="w-3 h-3" />
                            Nouvel utilisateur
                          </button>
                        </div>
                      </div>

                      {/* Liste des utilisateurs */}
                      {tenantUsers[tenant.tenant_id] ? (
                        <div className="space-y-2">
                          {tenantUsers[tenant.tenant_id].length === 0 ? (
                            <p className="text-xs text-slate-500 py-2">Aucun utilisateur</p>
                          ) : (
                            tenantUsers[tenant.tenant_id].map((u) => (
                              <div
                                key={u.user_id}
                                className={`flex items-center justify-between bg-slate-800/50 rounded-lg px-3 py-2 ${
                                  u.is_active === false ? "opacity-50" : ""
                                }`}
                              >
                                <div className="flex items-center gap-3">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                    u.is_active === false ? "bg-red-500/20" : "bg-blue-500/20"
                                  }`}>
                                    <Users className={`w-4 h-4 ${
                                      u.is_active === false ? "text-red-400" : "text-blue-400"
                                    }`} />
                                  </div>
                                  <div>
                                    <div className="flex items-center gap-2">
                                      <p className="text-sm text-white">{u.name || u.email}</p>
                                      {u.is_active === false && (
                                        <span className="text-xs text-red-400 bg-red-500/20 px-1.5 py-0.5 rounded">
                                          Desactive
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-xs text-slate-400">{u.email}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="px-2 py-0.5 bg-slate-700 text-slate-300 text-xs rounded">
                                    {u.role}
                                  </span>
                                  {/* Bouton Reset Password */}
                                  <button
                                    data-testid={`reset-pwd-${u.user_id}`}
                                    onClick={() => handleResetPassword(tenant.tenant_id, u.user_id, u.email)}
                                    disabled={actionLoading === `reset-${u.user_id}`}
                                    className="p-1.5 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 rounded transition-colors"
                                    title="Reinitialiser mot de passe"
                                  >
                                    {actionLoading === `reset-${u.user_id}` ? (
                                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                    ) : (
                                      <Key className="w-3.5 h-3.5" />
                                    )}
                                  </button>
                                  {/* Bouton Toggle Status */}
                                  <button
                                    data-testid={`toggle-status-${u.user_id}`}
                                    onClick={() => handleToggleUserStatus(tenant.tenant_id, u.user_id, u.is_active, u.email)}
                                    disabled={actionLoading === `status-${u.user_id}`}
                                    className={`p-1.5 rounded transition-colors ${
                                      u.is_active === false
                                        ? "bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400"
                                        : "bg-red-500/20 hover:bg-red-500/30 text-red-400"
                                    }`}
                                    title={u.is_active === false ? "Activer" : "Desactiver"}
                                  >
                                    {actionLoading === `status-${u.user_id}` ? (
                                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                    ) : u.is_active === false ? (
                                      <UserCheck className="w-3.5 h-3.5" />
                                    ) : (
                                      <UserX className="w-3.5 h-3.5" />
                                    )}
                                  </button>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      ) : (
                        <p className="text-xs text-slate-500 py-2">
                          Cliquez sur "Charger" pour voir les utilisateurs
                        </p>
                      )}

                      {/* Formulaire creation utilisateur */}
                      {showUserForm === tenant.tenant_id && (
                        <div className="mt-4 p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                          <h5 className="text-sm font-medium text-blue-400 mb-3 flex items-center gap-2">
                            <Plus className="w-4 h-4" />
                            Creer un utilisateur pour {tenant.name}
                          </h5>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs text-slate-400 mb-1">Email *</label>
                              <input
                                type="email"
                                value={userForm.email}
                                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                                placeholder="user@example.com"
                                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded text-sm text-white"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-slate-400 mb-1">Mot de passe *</label>
                              <input
                                type="text"
                                value={userForm.password}
                                onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                                placeholder="mot_de_passe"
                                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded text-sm text-white"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-slate-400 mb-1">Nom complet *</label>
                              <input
                                type="text"
                                value={userForm.name}
                                onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                                placeholder="Jean Dupont"
                                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded text-sm text-white"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-slate-400 mb-1">Role</label>
                              <select
                                value={userForm.role}
                                onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded text-sm text-white"
                              >
                                <option value="user">User</option>
                                <option value="admin">Admin</option>
                                <option value="owner">Owner</option>
                                <option value="manager">Manager</option>
                                <option value="commercial">Commercial</option>
                                <option value="accounting">Accounting</option>
                                <option value="viewer">Viewer</option>
                              </select>
                            </div>
                          </div>
                          <div className="flex gap-2 mt-4">
                            <button
                              onClick={() => setShowUserForm(null)}
                              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded text-sm"
                            >
                              Annuler
                            </button>
                            <button
                              data-testid={`create-user-btn-${tenant.tenant_id}`}
                              onClick={() => handleCreateUser(tenant.tenant_id)}
                              disabled={userFormLoading}
                              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 rounded text-sm flex items-center gap-2"
                            >
                              {userFormLoading ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <Check className="w-4 h-4" />
                              )}
                              Creer l'utilisateur
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 mt-4 pt-4 border-t border-slate-700/50">
                      {tenant.billing_status !== "active" && (
                        <button
                          data-testid={`activate-${tenant.tenant_id}`}
                          onClick={() => handleForceActivate(tenant.tenant_id)}
                          disabled={actionLoading === tenant.tenant_id}
                          className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-sm font-medium"
                        >
                          {actionLoading === tenant.tenant_id ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <Play className="w-4 h-4" />
                          )}
                          Activer manuellement
                        </button>
                      )}

                      {tenant.billing_status === "active" && (
                        <button
                          data-testid={`suspend-${tenant.tenant_id}`}
                          onClick={() => handleSuspend(tenant.tenant_id)}
                          disabled={actionLoading === tenant.tenant_id}
                          className="flex items-center gap-2 px-4 py-2 bg-red-600/80 hover:bg-red-600 rounded-lg text-sm"
                        >
                          {actionLoading === tenant.tenant_id ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <Pause className="w-4 h-4" />
                          )}
                          Suspendre
                        </button>
                      )}

                      {tenant.billing_status === "suspended" && (
                        <button
                          data-testid={`reactivate-${tenant.tenant_id}`}
                          onClick={() => handleReactivate(tenant.tenant_id)}
                          disabled={actionLoading === tenant.tenant_id}
                          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-sm"
                        >
                          {actionLoading === tenant.tenant_id ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <Play className="w-4 h-4" />
                          )}
                          Reactiver
                        </button>
                      )}

                      {/* Bouton Supprimer (sauf tenant_default) */}
                      {tenant.tenant_id !== "tenant_default" && (
                        deleteConfirm === tenant.tenant_id ? (
                          <div className="flex items-center gap-2 ml-auto">
                            <span className="text-xs text-red-400">Confirmer la suppression ?</span>
                            <button
                              data-testid={`confirm-delete-${tenant.tenant_id}`}
                              onClick={() => handleDeleteTenant(tenant.tenant_id)}
                              disabled={actionLoading === `delete-${tenant.tenant_id}`}
                              className="flex items-center gap-1 px-3 py-2 bg-red-600 hover:bg-red-500 rounded-lg text-sm font-medium"
                            >
                              {actionLoading === `delete-${tenant.tenant_id}` ? (
                                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <Trash2 className="w-3.5 h-3.5" />
                              )}
                              Oui, supprimer
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(null)}
                              className="flex items-center gap-1 px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm"
                            >
                              Annuler
                            </button>
                          </div>
                        ) : (
                          <button
                            data-testid={`delete-${tenant.tenant_id}`}
                            onClick={() => setDeleteConfirm(tenant.tenant_id)}
                            className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-red-600/80 rounded-lg text-sm ml-auto transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                            Supprimer
                          </button>
                        )
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
